/**
 * TSS Shared Utilities
 *
 * Common utility functions used across both Coach and Athlete apps.
 */

/**
 * Format a file size in bytes to a human-readable string
 */
export function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
}

/**
 * Format a duration in seconds to a human-readable string (e.g., "1:30")
 */
export function formatDuration(seconds: number): string {
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

/**
 * Format a duration in seconds to a longer format (e.g., "1 min 30 sec")
 */
export function formatDurationLong(seconds: number): string {
  if (seconds < 60) {
    return `${Math.floor(seconds)} sec`;
  }
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  if (secs === 0) {
    return `${mins} min`;
  }
  return `${mins} min ${secs} sec`;
}

/**
 * Format a date string to a relative time (e.g., "2 hours ago")
 * Handles ISO strings with or without timezone suffix (assumes UTC if missing)
 */
export function formatRelativeTime(dateString: string): string {
  // If the date string doesn't have timezone info, assume UTC
  let normalizedDateString = dateString;
  if (
    dateString &&
    !dateString.endsWith('Z') &&
    !dateString.includes('+') &&
    !dateString.includes('-', 10)
  ) {
    normalizedDateString = dateString + 'Z';
  }

  const date = new Date(normalizedDateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffSeconds = Math.floor(diffMs / 1000);
  const diffMinutes = Math.floor(diffSeconds / 60);
  const diffHours = Math.floor(diffMinutes / 60);
  const diffDays = Math.floor(diffHours / 24);

  // Handle invalid dates or future dates
  if (isNaN(diffMs) || diffSeconds < 0) {
    return 'just now';
  }

  if (diffSeconds < 60) {
    return 'just now';
  } else if (diffMinutes < 60) {
    return `${diffMinutes} min ago`;
  } else if (diffHours < 24) {
    return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
  } else if (diffDays < 7) {
    return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
  } else {
    return date.toLocaleDateString();
  }
}

/**
 * Format a date string to a short date (e.g., "Jan 1, 2024")
 */
export function formatShortDate(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

/**
 * Format a date string to a full date with time
 */
export function formatFullDate(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

/**
 * Calculate a person's age in whole years from a date-of-birth string.
 *
 * Returns null for missing, unparseable, or future-dated input.
 * Comparisons happen in UTC so a `YYYY-MM-DD` DOB doesn't drift across timezones.
 */
export function calculateAge(dob: string | null | undefined): number | null {
  if (!dob) return null;
  const birth = new Date(dob);
  if (isNaN(birth.getTime())) return null;

  const now = new Date();
  let age = now.getUTCFullYear() - birth.getUTCFullYear();

  const monthDiff = now.getUTCMonth() - birth.getUTCMonth();
  if (monthDiff < 0 || (monthDiff === 0 && now.getUTCDate() < birth.getUTCDate())) {
    age--;
  }

  return age < 0 ? null : age;
}

/**
 * Get user's display name from profile data
 */
export function getDisplayName(
  profile: {
    given_name?: string | null;
    family_name?: string | null;
    email?: string;
  } | null
): string {
  if (!profile) return '';
  const name = `${profile.given_name || ''} ${profile.family_name || ''}`.trim();
  return name || profile.email || '';
}

/**
 * Get user's initials from profile data
 */
export function getInitials(
  profile: {
    given_name?: string | null;
    family_name?: string | null;
    email?: string;
  } | null
): string {
  if (!profile) return '?';
  const first = profile.given_name?.[0] || profile.email?.[0] || '';
  const last = profile.family_name?.[0] || '';
  return (first + last).toUpperCase() || '?';
}

/**
 * Truncate text to a maximum length with ellipsis
 */
export function truncate(text: string, maxLength: number): string {
  if (text.length <= maxLength) return text;
  return text.slice(0, maxLength - 3) + '...';
}

/**
 * Generate a random ID
 */
export function generateId(): string {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
}

/**
 * Debounce a function
 */
export function debounce<T extends (...args: unknown[]) => unknown>(
  fn: T,
  delay: number
): (...args: Parameters<T>) => void {
  let timeoutId: ReturnType<typeof setTimeout>;
  return (...args: Parameters<T>) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => fn(...args), delay);
  };
}

/**
 * Check if a user has coach-level access (OWNER, COACH, or MODERATOR)
 */
export function isCoachOrAbove(role: string): boolean {
  return ['OWNER', 'COACH', 'MODERATOR'].includes(role);
}

/**
 * Check if a user has owner-level access
 */
export function isOwner(role: string): boolean {
  return role === 'OWNER';
}

/**
 * Check if a user can manage programs (OWNER or COACH)
 */
export function canManagePrograms(role: string): boolean {
  return ['OWNER', 'COACH'].includes(role);
}

/**
 * Check if a user can review videos (OWNER, COACH, or MODERATOR)
 */
export function canReviewVideos(role: string): boolean {
  return ['OWNER', 'COACH', 'MODERATOR'].includes(role);
}

/**
 * Check if user is a super admin
 */
export function isSuperAdmin(platformRole: string): boolean {
  return platformRole === 'SUPER_ADMIN';
}

/**
 * Check if user has platform admin access
 */
export function isPlatformAdmin(platformRole: string): boolean {
  return ['SUPER_ADMIN', 'SUPPORT'].includes(platformRole);
}

/**
 * Parse video tags from a comma-separated string
 */
export function parseTags(tagString: string): string[] {
  return tagString
    .split(',')
    .map((t) => t.trim().toLowerCase())
    .filter((t) => t.length > 0);
}

/**
 * Format tags for display (join with commas)
 */
export function formatTags(tags: string[]): string {
  return tags.join(', ');
}

/**
 * Calculate progress percentage
 */
export function calculateProgress(completed: number, total: number): number {
  if (total === 0) return 0;
  return Math.round((completed / total) * 100);
}

/**
 * Sleep for a given number of milliseconds
 */
export function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Classnames utility for conditional class strings
 */
export function cn(...classes: (string | boolean | undefined | null)[]): string {
  return classes.filter(Boolean).join(' ');
}

/**
 * Generate a clean, readable title from a video filename
 *
 * Cleans up common video filename patterns:
 * - VID_20231215_142530.mp4 → "Video Dec 15, 2023"
 * - IMG_1234.MOV → "Video 1234"
 * - my_cool_video.mp4 → "My Cool Video"
 * - screen-recording-2023-12-15.mp4 → "Screen Recording Dec 15, 2023"
 */
export function generateVideoTitle(filename: string): string {
  // Remove file extension
  let title = filename.replace(/\.[^/.]+$/, '');

  // Common patterns to clean up
  const patterns: Array<{ regex: RegExp; replacer: (match: RegExpMatchArray) => string }> = [
    // VID_YYYYMMDD_HHMMSS or VIDEO_YYYYMMDD_HHMMSS (Android style)
    {
      regex: /^(?:VID|VIDEO)[_-](\d{4})(\d{2})(\d{2})[_-](\d{2})(\d{2})(\d{2})$/i,
      replacer: (m) => formatVideoDate(m[1], m[2], m[3]),
    },
    // VID_YYYYMMDD or VIDEO_YYYYMMDD
    {
      regex: /^(?:VID|VIDEO)[_-](\d{4})(\d{2})(\d{2})$/i,
      replacer: (m) => formatVideoDate(m[1], m[2], m[3]),
    },
    // IMG_XXXX (iOS style) - just a number
    {
      regex: /^IMG[_-](\d+)$/i,
      replacer: (m) => `Video ${m[1]}`,
    },
    // Screen Recording YYYY-MM-DD... or screen_recording_YYYY-MM-DD...
    {
      regex: /^screen[_\s-]*recording[_\s-]*(\d{4})[_-](\d{2})[_-](\d{2})/i,
      replacer: (m) => `Screen Recording ${formatVideoDate(m[1], m[2], m[3])}`,
    },
    // YYYY-MM-DD_HH-MM-SS or YYYY_MM_DD_HH_MM_SS at start
    {
      regex: /^(\d{4})[_-](\d{2})[_-](\d{2})[_-](\d{2})[_-](\d{2})[_-](\d{2})$/,
      replacer: (m) => formatVideoDate(m[1], m[2], m[3]),
    },
    // YYYYMMDD_HHMMSS at start (no separators in date)
    {
      regex: /^(\d{4})(\d{2})(\d{2})[_-](\d{2})(\d{2})(\d{2})$/,
      replacer: (m) => formatVideoDate(m[1], m[2], m[3]),
    },
  ];

  // Try each pattern
  for (const { regex, replacer } of patterns) {
    const match = title.match(regex);
    if (match) {
      return replacer(match);
    }
  }

  // No pattern matched - do general cleanup
  // Replace underscores and dashes with spaces
  title = title.replace(/[_-]+/g, ' ');

  // Remove common prefixes that aren't helpful
  title = title.replace(/^(?:vid|video|mov|clip)\s+/i, '');

  // Clean up multiple spaces
  title = title.replace(/\s+/g, ' ').trim();

  // If we end up with just a timestamp, make it nicer
  const timestampMatch = title.match(
    /^(\d{4})\s*(\d{2})\s*(\d{2})(?:\s+(\d{2})\s*(\d{2})\s*(\d{2}))?$/
  );
  if (timestampMatch) {
    return formatVideoDate(timestampMatch[1], timestampMatch[2], timestampMatch[3]);
  }

  // Title case the result if it looks like it needs it
  if (title === title.toLowerCase() || title === title.toUpperCase()) {
    title = toTitleCase(title);
  }

  // If title is empty or just whitespace, provide a default
  if (!title.trim()) {
    return 'Untitled Video';
  }

  return title;
}

/**
 * Format a date from year/month/day strings into a readable format
 */
function formatVideoDate(year: string, month: string, day: string): string {
  const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
  if (isNaN(date.getTime())) {
    return 'Video';
  }
  return `Video ${date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;
}

/**
 * Convert a string to title case
 */
function toTitleCase(str: string): string {
  return str.replace(/\b\w+/g, (word) => {
    // Keep small words lowercase unless they're at the start
    const smallWords = [
      'a',
      'an',
      'the',
      'and',
      'but',
      'or',
      'for',
      'nor',
      'on',
      'at',
      'to',
      'by',
      'of',
    ];
    if (smallWords.includes(word.toLowerCase())) {
      return word.toLowerCase();
    }
    return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
  });
}
